/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * 패널 클래스를 정의한다.
 *
 * @file /src/components/tab/Tab.ts
 * @author Arzz <arzz@arzz.com>
 * @license MIT License
 * @modified 2026. 9. 25.
 */
import * as parent from '@components/panel/Panel';
import { Menu } from '@components/menu/Menu';
import { Tabbar } from './elements/Tabbar';
import { Exceptions } from '@utils/Exceptions';
import './Tab.css';

export interface Listeners<T> extends parent.Listeners<T> {
    /**
     * 탭이 활성화되기 직전
     */
    beforeActive?: (tab: parent.Panel, tabs: T) => void;

    /**
     * 활성화된 탭이 변경되거나, 탭이 활성화되었을 때
     */
    active?: (tab: parent.Panel, tabs: T) => void;

    /**
     * 탭 순서가 변경되었을 때
     */
    sorter?: (list: { name?: string; title: string; icon: string }[]) => void;

    /**
     * 탭 메뉴가 보였을 때
     */
    openMenu?: (menu: Menu, tab: parent.Panel, tabs: T) => void;

    /**
     * 탭이 닫혔을 때
     */
    close?: (tab: parent.Panel, tabs: T) => void;
}

export interface Properties<T> extends parent.Properties<T> {
    /**
     * 내부 패널
     */
    items?: parent.Panel[];

    /**
     * 기본적으로 활성할 탭 인덱스
     */
    activeTab?: number;

    /**
     * 탭바 위치
     */
    tabPosition?: 'top' | 'bottom' | 'hidden';

    /**
     * 탭바 크기
     */
    tabSize?: 'default' | 'small';

    /**
     * 이벤트리스너
     */
    listeners?: Listeners<T>;
}

export class Tab extends parent.Panel {
    /**
     * 컴포넌트 타입
     */
    type = 'panel';

    /**
     * 컴포넌트 확장타입
     */
    variant = 'tab';

    /**
     * 기본적으로 활성할 탭 인덱스
     */
    activeTab: number | null = null;

    /**
     * 활성화 탭 Id
     */
    activeTabId: string | null = null;

    /**
     * 탭바 위치
     */
    tabPosition: 'top' | 'bottom' | 'hidden' | null = null;

    /**
     * 탭바 크기
     */
    tabSize: 'default' | 'small' | null = null;

    /**
     * 직전 작업
     */
    prevActiveTab: parent.Panel = null;

    /**
     * 탭 변경 허용 여부 (beforeActive 리스너에서 false 로 설정하면 탭 변경을 막는다)
     */
    allowActive: boolean = true;

    /**
     * 탭바
     */
    tabbar: Tabbar = null;

    /**
     * 탭 패널 클래스를 정의한다.
     *
     * @param properties - 초기설정
     */
    constructor(public properties: Properties<Tab> = null) {
        super(properties);

        this.activeTab = this.properties.activeTab ?? null;
        this.activeTabId = null;

        if (this.properties.tabPosition == 'hidden') {
            this.tabPosition = 'hidden';
        } else {
            this.tabPosition = this.properties.tabPosition ?? 'bottom';
        }
        this.tabSize = this.properties.tabSize ?? 'default';
        this.scrollable = false;

        this.addEvent('resize', () => {
            this.tabbar?.getScroller().updatePosition();
        });
    }

    /**
     * 탭패널의 하위 컴포넌트를 정의한다.
     */
    initItems(): void {
        super.initItems();

        for (let item of this.items ?? []) {
            if (item instanceof parent.Panel) {
                item.hide();
                item.getTitle()?.hide();
            }
        }
    }

    /**
     * 탭바 엘리먼트를 가져온다.
     *
     * @returns 탭바 엘리먼트
     */
    getTabbar(): Tabbar {
        if (this.tabbar === null) {
            this.tabbar = new Tabbar({ size: this.tabSize });
            this.tabbar.setComponent(this);
        }

        return this.tabbar;
    }

    /**
     * 특정탭 패널을 가져온다.
     *
     * @param id - 가져올 탭 패널 고유값 또는 탭 패널 인덱스
     * @returns 탭 패널의 패널객체
     */
    getTab<T extends parent.Panel = parent.Panel>(id: string | number): T {
        let tabId = null;
        if (typeof id == 'number') {
            tabId = this.items[id]?.getId();
        } else {
            tabId = id;
        }

        if (tabId == null) return null;
        const panel = this.items.find((item) => item.getId() === tabId);
        if (panel !== null) {
            return panel as T;
        }
        return null;
    }

    /**
     * 활성화된 탭패널을 가져온다.
     *
     * @returns 활성화된 탭 패널객체
     */
    getActiveTab<T extends parent.Panel = parent.Panel>(): T {
        return this.getTab(this.activeTabId) as T;
    }

    /**
     * 특정탭을 활성화한다.
     *
     * @param id - 활성화할 탭 고유값 또는 탭 인덱스
     * @param is_fire_event - 이벤트발생여부
     */
    async active(id: string | number, is_fire_event: boolean = true): Promise<void> {
        const tab = this.getTab(id);
        if (tab === null) {
            return;
        }

        if (tab.getId() !== this.activeTabId) {
            this.prevActiveTab = this.getTab(this.activeTabId);
            if (is_fire_event === true) {
                await this.onBeforeActive(tab);
            }

            if (this.allowActive === false) {
                return;
            }

            this.activeTabId = tab.getId();
            for (const item of this.items ?? []) {
                if (item.isShow() == true) {
                    // @todo disconnect ?
                    await item.hide();
                }
            }

            // @todo connect ?
            await tab.show();

            this.getTabbar().active(tab.getId());

            if (is_fire_event === true) {
                await this.onActive(tab);
            }
        }
    }

    /**
     * 직전에 활성화된 패널이 있다면, 해당 패널을 활성화한다.
     *
     * @param is_closed - 현재 활성화된 창이 제거되면서 이전작업을 복원하는지 여부
     */
    async activeLatest(is_closed: boolean = false): Promise<void> {
        if (this.prevActiveTab !== null) {
            await this.active(this.prevActiveTab.getId());
        } else {
            Exceptions.debug('[Tab]', '이전탭이 존재하지 않음');
            await this.active(0);
        }

        if (is_closed == true) {
            this.prevActiveTab = null;
        }
    }

    /**
     * 자식 컴포넌트를 추가한다.
     *
     * @param component - 추가할 컴포넌트
     * @param index - 추가할 위치 (정의되지 않은 경우 제일 마지막에 추가한다)
     */
    async appendItem(item: parent.Panel, index: number | undefined = undefined): Promise<void> {
        item.hide();
        item.getTitle()?.hide();

        await this.getTabbar().append(item, index);
        await super.appendItem(item, index);
    }

    /**
     * 자식 컴포넌트를 제거한다.
     *
     * @param Panel - 제거할 자식 컴포넌트
     * @param removeDom - 자식의 컴포넌트 DOM 을 제거할지 여부 (기본값 : true)
     */
    async removeItem(item: parent.Panel, removeDom: boolean = true): Promise<void> {
        await super.removeItem(item, removeDom);
        await this.getTabbar().detach(item);

        await this.onClose(item);

        if (this.activeTabId == item.getId()) {
            this.activeLatest(true);
        }
    }

    /**
     * 컴포넌트 DOM 을 렌더링한다.
     */
    async renderDom(): Promise<void> {
        await super.renderDom();
        this.setLoading(false);
    }

    /**
     * 헤더를 렌더링한다.
     */
    protected async renderHeader(): Promise<void> {
        await super.renderHeader();

        if (this.tabPosition == 'top') {
            const $top = this.$getHeader();
            $top.append(this.getTabbar().$getElement());
            await this.getTabbar().render();
        }
    }

    /**
     * 푸터를 렌더링한다.
     */
    protected async renderFooter(): Promise<void> {
        await super.renderFooter();

        if (this.tabPosition == 'bottom') {
            const $bottom = this.$getFooter();
            $bottom.append(this.getTabbar().$getElement());
            await this.getTabbar().render();
        }
    }

    /**
     * 컴포넌트 렌더링이 완료된 이후 초기상태정의가 필요할 경우, 상태를 렌더링한다.
     */
    async renderState(): Promise<void> {
        if (this.activeTab !== null) {
            await this.active(this.activeTab);
        }

        await super.renderState();
    }

    /**
     * 컴포넌트를 제거한다.
     *
     * @param removeDom - 컴포넌트 DOM 을 제거할지 여부 (기본값 : true)
     */
    async remove(removeDom: boolean = true): Promise<void> {
        this.tabbar?.remove(false);
        await super.remove(removeDom);
    }

    /**
     * 탭이 활성화되기 직전
     *
     * @param panel - 활성화될 탭패널
     */
    async onBeforeActive(panel: parent.Panel): Promise<void> {
        this.allowActive = true;
        await this.fireEvent('beforeActive', [panel, this]);
    }

    /**
     * 활성화된 탭이 변경되었을 때
     *
     * @param panel - 활성화된 탭패널
     */
    async onActive(panel: parent.Panel): Promise<void> {
        await this.fireEvent('active', [panel, this]);
    }

    /**
     * 탭이 닫혔을 때
     *
     * @param panel - 활성화된 탭패널
     */
    async onClose(panel: parent.Panel): Promise<void> {
        await this.fireEvent('close', [panel, this]);
    }
}
